import { createSlice } from "@reduxjs/toolkit";
import { doc, writeBatch } from "firebase/firestore";
import { firestore } from "contexts/FirebaseContext";

const initialState = { campaigns: [], campaign: null, error: null };

const smartlead = createSlice({
  name: "smartlead",
  initialState,
  reducers: {
    getAll(state, action) {
      state.campaigns = action.payload;
    },
    get(state, action) {
      state.campaign = action.payload;
    },
  },
});

export default smartlead.reducer;
export const { get, getAll } = smartlead.actions;

const apiKey = "24b6b3f7-74bb-4c94-b00b-7227bf604306_hda7wce";

const monthNames = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

const fetchApi = async (url) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
  return response.json();
};

/**
 * PRODUCTION LOGIC: Sync Today + Last 3 Days (4 Days Total)
 * Aggregates data from two specific campaigns
 */
export const syncSmartleadToFirestore = async () => {
  // 1. Updated Campaign List
  const CAMPAIGNS = [
    { id: 3073570, name: "Kotisivujen päivitys 1000€ - Joose" },
    { id: 3073550, name: "Kotisivujen päivitys 1000€" },
    { id: 3003069, name: "Kotisivujen päivitys - Joose" },
    { id: 3003064, name: "Uudet kotisivut (domain) - Joose" },
    { id: 3003060, name: "Uudet kotisivut (puhelu) - Joose" },
    { id: 3511220, name: "Soitettu alkuvuodesta" },
    { id: 2989423, name: "Uudet kotisivut (puhelu)" },
    { id: 2989418, name: "Uudet kotisivut (domain)" },
    { id: 2788085, name: "Kotisivujen päivitys" },
    { id: 2122237, name: "Uudet kotisivut" },
  ];

  try {
    const batch = writeBatch(firestore);

    // Loop through the last 4 days (0 to 3)
    for (let i = 0; i <= 3; i++) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split("T")[0];

      // Object to hold individual campaign stats for this specific date
      const dailyStats = {};

      for (const campaign of CAMPAIGNS) {
        const url = `https://server.smartlead.ai/api/v1/campaigns/${campaign.id}/analytics-by-date?api_key=${apiKey}&start_date=${dateStr}&end_date=${dateStr}`;

        try {
          const data = await fetchApi(url);
          const sentCount = parseInt(data.sent_count || 0);

          // Store count under the campaign name
          // Replacing dots or slashes in names if they exist to avoid Firestore path issues
          const safeName = campaign.name.replace(/\./g, "_");
          dailyStats[safeName] = sentCount;
        } catch (dayError) {
          console.warn(`Failed to fetch for ${campaign.name} on ${dateStr}`);
        }
      }

      const year = date.getFullYear();
      const month = monthNames[date.getMonth()];
      const docRef = doc(firestore, `data/outreach/data_${year}/${month}`);

      // 2. Save the granular data
      // Structure: [dateStr]: { "Campaign Name": 50, "Other Campaign": 30 }
      batch.set(
        docRef,
        {
          [dateStr]: dailyStats,
        },
        { merge: true }
      );
    }

    await batch.commit();
    console.log("Outreach Sync Complete (Granular)");
  } catch (error) {
    console.error("Outreach Sync Failed:", error);
  }
};

/**
 * Add a single lead to a specific campaign with exact schema mapping
 * @param {object} rowData - The full row object from ListPage
 * @param {number} campaignId - The specific Smartlead campaign ID
 */
export const addLeadToTargetCampaign = async (rowData, campaignId) => {
  const url = `https://server.smartlead.ai/api/v1/campaigns/${campaignId}/leads?api_key=${apiKey}`;

  const payloadLead = {};

  // 1. Map Native Fields (Standard Smartlead Schema)
  if (rowData.email_address) payloadLead.email = String(rowData.email_address).trim();
  if (rowData.name) payloadLead.company_name = String(rowData.name).trim();
  if (rowData.phone_number) payloadLead.phone_number = String(rowData.phone_number).trim();
  if (rowData.website) payloadLead.website = String(rowData.website).trim();

  // 2. Map everything else to custom_fields as Strings
  const customFields = {};
  const nativeKeys = ["email_address", "name", "phone_number", "website", "id"];

  Object.keys(rowData).forEach((key) => {
    const value = rowData[key];

    // Only process if it's not a native key and has a value
    if (value !== null && value !== undefined && value !== "" && !nativeKeys.includes(key)) {
      // Smartlead requires values to be Strings. We also skip Objects/Arrays
      if (typeof value !== "object") {
        customFields[key] = String(value);
      }
    }
  });

  if (Object.keys(customFields).length > 0) {
    payloadLead.custom_fields = customFields;
  }

  const payload = {
    lead_list: [payloadLead],
    settings: {
      ignore_global_block_list: false,
      ignore_unsubscribe_list: false,
      ignore_duplicate_leads_in_other_campaign: true,
    },
  };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      // Log the actual error body from Smartlead to see exactly which field failed
      const errorData = await response.json();
      console.error("Smartlead API Error Detail:", errorData);
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to add lead:", error);
    throw error;
  }
};
